import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { Modal, Fld, ConfirmButton } from "../ui/primitives.jsx";
import { useToast } from "../ui/Toasts.jsx";
import { fmtMoney } from "../lib/format.js";
import { rowToForm, useSaveMaintenance, useDeleteMaintenance, useEquipmentOptions } from "./api.js";

export default function MaintForm({ rec, onClose }) {
  const isNew = !rec?.id;
  const [f, setF] = useState(() => rowToForm(rec ?? {}));
  const [err, setErr] = useState("");
  const { notify } = useToast();

  const equipment = useEquipmentOptions();
  const save = useSaveMaintenance();
  const del = useDeleteMaintenance();
  const busy = save.isPending || del.isPending;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Mirrors the generated column in Postgres so the total is visible before saving.
  const costTotal =
    (Number(f.parts) || 0) + (Number(f.labor) || 0) + (Number(f.fuel) || 0) + (Number(f.other) || 0);

  async function onSave() {
    setErr("");
    if (!f.equipmentId) { setErr("Pick a machine."); return; }
    if (!f.date) { setErr("Pick a date."); return; }
    try {
      await save.mutateAsync({ id: rec?.id, form: f });
      notify(isNew ? "Service logged." : "Service record updated.");
      onClose();
    } catch (e) {
      setErr(e.message);
    }
  }

  async function onDelete() {
    setErr("");
    try {
      await del.mutateAsync(rec.id);
      notify("Service record deleted.");
      onClose();
    } catch (e) {
      setErr(e.message);
    }
  }

  return (
    <Modal
      title={isNew ? "Log service" : "Edit service record"}
      onClose={onClose}
      footer={
        <>
          {!isNew && (
            <ConfirmButton
              className="btn danger"
              disabled={busy}
              label={<><Trash2 size={15} /> Delete</>}
              confirmLabel="Click again to delete"
              onConfirm={onDelete}
            />
          )}
          <div style={{ flex: 1 }} />
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={onSave} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      {err && <div className="notice red">{err}</div>}

      <div className="frm">
        <Fld label="Machine">
          <select value={f.equipmentId} onChange={(e) => set("equipmentId", e.target.value)}>
            <option value="">— Select —</option>
            {(equipment.data ?? []).map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
        </Fld>
        <Fld label="Date">
          <input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} />
        </Fld>

        <Fld label="Type">
          <select value={f.type} onChange={(e) => set("type", e.target.value)}>
            <option value="service">Routine service</option>
            <option value="repair">Repair</option>
            <option value="inspection">Inspection</option>
          </select>
        </Fld>
        <Fld label="Hour meter reading" hint="Also updates the machine's hour meter.">
          <input value={f.hours} onChange={(e) => set("hours", e.target.value)} />
        </Fld>

        <Fld label="Performed by">
          <input
            value={f.performedBy}
            onChange={(e) => set("performedBy", e.target.value)}
            placeholder="Shop, dealer, or self"
          />
        </Fld>
        <Fld label="Next service due (date)" hint="Also updates the machine's next service date.">
          <input type="date" value={f.nextDue} onChange={(e) => set("nextDue", e.target.value)} />
        </Fld>

        <Fld label="Parts ($)">
          <input type="number" step="0.01" value={f.parts} onChange={(e) => set("parts", e.target.value)} />
        </Fld>
        <Fld label="Labor ($)">
          <input type="number" step="0.01" value={f.labor} onChange={(e) => set("labor", e.target.value)} />
        </Fld>
        <Fld label="Fuel ($)">
          <input type="number" step="0.01" value={f.fuel} onChange={(e) => set("fuel", e.target.value)} />
        </Fld>
        <Fld
          label="Other ($)"
          hint={`Cost total: ${fmtMoney(costTotal)} — computed by the database, and flows into Books as an itemized expense.`}
        >
          <input type="number" step="0.01" value={f.other} onChange={(e) => set("other", e.target.value)} />
        </Fld>

        <Fld full label="Work performed / notes">
          <textarea
            value={f.notes}
            onChange={(e) => set("notes", e.target.value)}
            placeholder="Oil + filters, greased pins, replaced hydraulic hose…"
          />
        </Fld>
      </div>
    </Modal>
  );
}
